import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Pagination,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { getEligibleGames, createOrUpdateGotySelection } from '../../services/goty_api';
import { GotyCategory, GotyCategoryLabels } from '../../types/Goty';
import { Game } from '../../types/Game';

interface GotyGameSelectDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  year: number;
  category: GotyCategory;
  currentGame?: Game | null;
  currentComment?: string;
  isMyGoty?: boolean;
}

const GotyGameSelectDialog: React.FC<GotyGameSelectDialogProps> = ({
  open,
  onClose,
  onSave,
  year,
  category,
  currentGame,
  currentComment = '',
  isMyGoty = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedGame, setSelectedGame] = useState<Game | null>(currentGame || null);
  const [comment, setComment] = useState(currentComment);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const categoryLabel = GotyCategoryLabels[category];

  useEffect(() => {
    if (open) {
      setSelectedGame(currentGame || null);
      setComment(currentComment);
      setSearchQuery('');
      setPage(1);
      fetchGames('', 1);
    }
  }, [open, currentGame, currentComment]);

  const fetchGames = async (query: string, pageNum: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getEligibleGames(year, query, pageNum);
      setGames(response.results);
      setTotalPages(Math.ceil(response.count / response.page_size));
    } catch (err) {
      console.error('Failed to fetch games:', err);
      setError('ゲームの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchGames(searchQuery, 1);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1);
    fetchGames('', 1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchGames(searchQuery, value);
  };

  const handleGameSelect = (game: Game) => {
    setSelectedGame(game);
  };

  const handleSave = async () => {
    if (!selectedGame) {
      setError('ゲームを選択してください');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await createOrUpdateGotySelection(year, category, selectedGame.game_id, comment, isMyGoty);
      onSave();
      onClose();
    } catch (err: any) {
      console.error('Failed to save selection:', err);
      setError(err.response?.data?.error || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {categoryLabel.ja} を選択
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {isMyGoty ? `${year}年に遊んだゲーム（発売年制約なし）` : `${year}年度対象ゲーム（${year - 1}/12/1 ～ ${year}/11/30）`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 検索バー */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="ゲームを検索..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* 選択中のゲーム */}
        {selectedGame && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              border: '2px solid',
              borderColor: 'primary.main',
              backgroundColor: 'rgba(102, 126, 234, 0.1)',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>
              選択中
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {selectedGame.cover && (
                <Box
                  component="img"
                  src={selectedGame.cover}
                  alt={selectedGame.name}
                  sx={{ width: 60, height: 80, objectFit: 'cover', borderRadius: 1 }}
                />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedGame.japanese_name || selectedGame.name}
                </Typography>
                {selectedGame.first_release_date && (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(selectedGame.first_release_date).toLocaleDateString('ja-JP')}
                  </Typography>
                )}
              </Box>
              <IconButton size="small" onClick={() => setSelectedGame(null)}>
                <ClearIcon />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* コメント入力 */}
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder={`${categoryLabel.ja}に選んだ理由やコメントを記入（任意）`}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          sx={{ mb: 3 }}
          inputProps={{ maxLength: 500 }}
          helperText={`${comment.length}/500`}
        />

        {/* ゲーム一覧 */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : games.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              ゲームが見つかりませんでした
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              {games.map((game) => (
                <Grid item xs={6} sm={4} md={3} key={game.game_id}>
                  <Card
                    onClick={() => handleGameSelect(game)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: selectedGame?.game_id === game.game_id ? '2px solid' : '1px solid transparent',
                      borderColor: selectedGame?.game_id === game.game_id ? 'primary.main' : 'transparent',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      }
                    }}
                  >
                    {game.cover && (
                      <CardMedia
                        component="img"
                        image={game.cover}
                        alt={game.name}
                        sx={{ aspectRatio: '3/4', objectFit: 'cover' }}
                      />
                    )}
                    <CardContent sx={{ p: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: '0.7rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {game.japanese_name || game.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          キャンセル
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!selectedGame || saving}
          startIcon={saving && <CircularProgress size={16} />}
        >
          {saving ? '保存中...' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GotyGameSelectDialog;
